import { queryOptions, useQuery } from "@tanstack/react-query";

import data from "../../assets/mocks/auction-cars.json";
import dataGoodwood from "../../assets/mocks/auction-cars_goodwood.json";
import { results } from "../../assets/mocks/auctions.json";
import { getCarDataFiltered } from "./utils";

import { env } from "@/config/env";
import { api } from "@/lib/api-client";
import { hasAccessToken } from "@/lib/auth";
import { AxiosMock } from "@/utils/mock";

import type { QueryConfig } from "@/lib/react-query";
import type { AuctionCar } from "@/types/auctions";
import type { PaginatedList } from "@/types/entity";
import type { CarQueryParams } from "./types";

if (import.meta.env.MODE === "test") {
  // Mocking the API response for auction cars
  const mock = AxiosMock.getInstance();
  // const uri = new RegExp("^\/carburate\/auctions\/[a-zA-Z0-9_-]+\/cars\/(\\?.*)?$");
  // mock.onGet(uri).reply(200, data);
  results.forEach((auction, index) => {
    const uri = new RegExp(`^\/carburate\/auctions\/${auction.id}\/cars\/(\\?.*)?$`);
    mock.onGet(uri).reply(200, index === 0 ? dataGoodwood : data);
  });
}

type AuctionCarQueryParams = CarQueryParams & {
  auctionId?: string | undefined;
};

export const getAuctionCars = ({ auctionId, page, pageSize }: AuctionCarQueryParams) => {
  return api.get(
    `/carburate/auctions/${auctionId}/cars/?page=${page}&page_size=${pageSize}`
  ) as Promise<PaginatedList<AuctionCar>>;
};

export const getAuctionCarsQueryOptions = ({
  auctionId,
  page,
  pageSize,
}: AuctionCarQueryParams) => {
  return queryOptions({
    queryKey: ["auctions", auctionId, "cars", { page, pageSize }],
    queryFn: () => getAuctionCars({ auctionId, page, pageSize }),
  });
};

type UseAuctionOptions = AuctionCarQueryParams & {
  queryConfig?: QueryConfig<typeof getAuctionCars>;
};

export const useAuctionCars = ({
  auctionId,
  parameters,
  queryConfig,
  page,
  pageSize,
}: UseAuctionOptions) => {
  const isClientPagination = env.PAGINATION === "client";

  const currentPage = isClientPagination ? 1 : page;
  const currentPageSize = isClientPagination ? 999999 : pageSize;

  let data: PaginatedList<AuctionCar>;

  const {
    data: response,
    isSuccess,
    ...props
  } = useQuery({
    enabled: hasAccessToken() && !!auctionId,
    ...getAuctionCarsQueryOptions({ auctionId, page: currentPage, pageSize: currentPageSize }),
    ...queryConfig,
  });

  data = response!;
  if (isClientPagination && isSuccess) {
    data = getCarDataFiltered(data, { parameters, page, pageSize });
  }

  return { data, isSuccess, ...props };
};
